import { Box, Stack, TextField, Typography } from '@mui/material';
import { ICaseDetailsInputs } from 'API';
import { CustomRadioGroup, FileUpload, TextareaCounter } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY, FileCategories } from 'Enum';
import { useDictionary } from 'Hooks';
import { IndexContext } from 'Modules/CaseDetails/CaseContext';
import { useContext, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { generateError } from 'Regex';
import { convertStringBoolToBoolean } from 'Utils/BooleanStringHandler';
import { validateLettersNumbersAndSpacialChars } from 'Utils/validateLettersNumbersAndSpacialChars';

export const DiagnosisDescOrFile = () => {
  const { index, fieldId } = useContext(IndexContext);
  const {
    register,
    formState: { errors },
  } = useFormContext<ICaseDetailsInputs>();

  const [
    textAreaDiseaseDetailsDescription,
    setTextAreaDiseaseDetailsDescription,
  ] = useState<string>('');

  const dictionary = useDictionary(Dictionary, DICTIONARY.CASE_DETAILS);

  const baseName = `caseDetails.${index}.diseaseKindSection`;

  const [isDiseaseDescribedInFile, diseaseDetailsDescription] = useWatch({
    name: [
      `${baseName}.isDiseaseDescribedInFile`,
      `${baseName}.diseaseDetailsDescription`,
    ],
  });

  const fileTextOptions = [
    {
      id: true,
      value: dictionary.secondSectionDiseaseDescOption1,
    },
    {
      id: false,
      value: dictionary.secondSectionDiseaseDescOption2,
    },
  ];

  const isDiseaseDescribedInFileBooleanValue = convertStringBoolToBoolean(
    isDiseaseDescribedInFile
  );

  const diseaseKindSectionServiceError = errors.caseDetails
    ? errors.caseDetails[index]?.diseaseKindSection
    : undefined;

  useEffect(() => {
    if (diseaseDetailsDescription)
      setTextAreaDiseaseDetailsDescription(diseaseDetailsDescription);
  }, [diseaseDetailsDescription]);

  return (
    <>
      <Stack mt='10px'>
        <Typography>{dictionary.secondSectionDiseaseDescSubTitle}</Typography>
        <CustomRadioGroup
          id={dictionary.secondSectionDiseaseDescSubTitle}
          hideId
          name={`${baseName}.isDiseaseDescribedInFile` as const}
          options={fileTextOptions.map(({ id, value }) => ({
            value: id.toString(),
            label: value,
          }))}
        />
      </Stack>
      {isDiseaseDescribedInFileBooleanValue && (
        <Stack gap='10px' sx={{ mt: '10px' }}>
          <Typography>{dictionary.secondSectionDiseaseDescFile}</Typography>
          <FileUpload
            caseDetailsId={fieldId}
            fileLabel={dictionary.secondSectionFileAria}
            fileCategoryId={FileCategories.DiseaseDescription}
            ariaLabel={dictionary.secondSectionFileAria}
            dataRequired
          />
        </Stack>
      )}
      {isDiseaseDescribedInFileBooleanValue === false && (
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <TextField
            id={`${dictionary.secondSectionDiseaseDescFile}-${index}`}
            label={dictionary.secondSectionDiseaseDescFile}
            sx={{ mt: '20px' }}
            inputProps={{
              maxLength: '500',
            }}
            {...register(
              `${baseName}.diseaseDetailsDescription` as `caseDetails.0.diseaseKindSection.diseaseDetailsDescription`
            )}
            multiline
            rows={6}
            onChange={(event: any) => {
              setTextAreaDiseaseDetailsDescription(event.target.value);
              validateLettersNumbersAndSpacialChars(event);
            }}
            role={
              Boolean(diseaseKindSectionServiceError?.diseaseDetailsDescription)
                ? 'alert'
                : undefined
            }
            error={Boolean(
              diseaseKindSectionServiceError?.diseaseDetailsDescription
            )}
            helperText={
              Boolean(
                diseaseKindSectionServiceError?.diseaseDetailsDescription
                  ?.message
              ) &&
              generateError(
                dictionary.secondSectionDiseaseDescFile,
                diseaseKindSectionServiceError?.diseaseDetailsDescription
                  ?.message!
              )
            }
          />
          <TextareaCounter
            counter={textAreaDiseaseDetailsDescription.length}
            limit={500}
            hasError={Boolean(
              diseaseKindSectionServiceError?.diseaseDetailsDescription?.message
            )}
          />
        </Box>
      )}
    </>
  );
};
